// src/lib/remoteProfiles.js
// Zentrale API fürs Laden/Speichern von Profilen, Fortschritt & Settings in Supabase.
// Nutzt RLS: der eingeloggte User darf nur seine eigenen Zeilen sehen/bearbeiten.

import { supabase } from "../supabaseClient";

/**
 * Profile laden (Liste von { profile_id, name }).
 * @param {string} userId - Supabase user.id (UUID)
 * @returns {Promise<Array<{profile_id: string, name: string}>>}
 */
export async function fetchProfiles(userId) {
  if (!userId) throw new Error("fetchProfiles: missing userId");
  const { data, error } = await supabase
    .from("user_profiles")
    .select("profile_id,name")
    .eq("user_id", userId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Profil anlegen/aktualisieren (Upsert).
 * PK: (user_id, profile_id)
 * @param {string} userId
 * @param {{id: string, name: string}} profile
 */
export async function upsertProfile(userId, profile) {
  if (!userId) throw new Error("upsertProfile: missing userId");
  if (!profile?.id || !profile?.name) {
    throw new Error("upsertProfile: profile.id and profile.name required");
  }

  const row = {
    user_id: userId,
    profile_id: profile.id,
    name: profile.name,
  };

  const { error } = await supabase
    .from("user_profiles")
    .upsert(row, { onConflict: "user_id,profile_id" }); // nutzt den PK
  if (error) throw error;
}

/**
 * Profil löschen (optional).
 * Löscht durch FK-Cascade auch den Fortschritt dieses Profils.
 * @param {string} userId
 * @param {string} profileId
 */
export async function deleteProfile(userId, profileId) {
  if (!userId) throw new Error("deleteProfile: missing userId");
  if (!profileId) throw new Error("deleteProfile: missing profileId");

  const { error } = await supabase
    .from("user_profiles")
    .delete()
    .eq("user_id", userId)
    .eq("profile_id", profileId);

  if (error) throw error;
}

/**
 * Fortschritt laden (JSON payload) für ein Profil.
 * Struktur ist flexibel: du speicherst ein Objekt mit Sprach-Keys:
 * {
 *   "Spanish->German": { "es-1": {...}, ... },
 *   "English->German": { ... }
 * }
 * @param {string} userId
 * @param {string} profileId
 * @returns {Promise<Object>} payload
 */
export async function loadProgress(userId, profileId) {
  if (!userId) throw new Error("loadProgress: missing userId");
  if (!profileId) throw new Error("loadProgress: missing profileId");

  const { data, error } = await supabase
    .from("profile_progress")
    .select("payload")
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .maybeSingle();

  // PGRST116 = not found (ok, wenn es noch keinen Eintrag gibt)
  if (error && error.code !== "PGRST116") throw error;
  return data?.payload || {};
}

/**
 * Fortschritt speichern (payload JSON) für ein Profil.
 * Upsert, damit es sowohl bei Erstschreiben als auch Updates klappt.
 * @param {string} userId
 * @param {string} profileId
 * @param {Object} payload
 */
export async function saveProgress(userId, profileId, payload) {
  if (!userId) throw new Error("saveProgress: missing userId");
  if (!profileId) throw new Error("saveProgress: missing profileId");

  const { error } = await supabase
    .from("profile_progress")
    .upsert(
      { user_id: userId, profile_id: profileId, payload },
      { onConflict: "user_id,profile_id" }
    );

  if (error) throw error;
}

/**
 * (Optional) Hilfsfunktion:
 * Stellt sicher, dass mind. ein Profil ("default") existiert und liefert die Liste.
 * Nutze das z.B. direkt nach Login, um den ersten Sync zu machen.
 * @param {string} userId
 * @param {{id:string,name:string}[]=} initialLocalProfiles  Fallback (z. B. lokale Profile)
 */
export async function ensureDefaultProfile(
  userId,
  initialLocalProfiles = [{ id: "default", name: "Standard" }]
) {
  const remote = await fetchProfiles(userId);
  if (remote.length > 0) return remote;

  // Keine Profile vorhanden → initiale anlegen (z. B. aus localStorage import)
  await Promise.all(initialLocalProfiles.map((p) => upsertProfile(userId, p)));
  return fetchProfiles(userId);
}

/* ---------------------------------------------------------------------- */
/*   Profile Settings (Sprache, Tempo, Weekly Words)                      */
/* ---------------------------------------------------------------------- */

/**
 * Lädt die gespeicherten Einstellungen für ein Profil:
 * - languageId (z.B. "Spanish->German")
 * - pace ("easy" | "medium" | "hard")
 * - weeklyWords (z.B. 100)
 *
 * Gibt `null` zurück, wenn noch keine Settings existieren.
 */
export async function loadProfileSettings(userId, profileId) {
  if (!userId || !profileId) {
    throw new Error("loadProfileSettings: missing userId or profileId");
  }

  const { data, error } = await supabase
    .from("profile_settings")
    .select("language_id, pace, weekly_words")
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .maybeSingle();

  // PGRST116 = not found → okay, dann gibt es einfach noch keinen Datensatz
  if (error && error.code !== "PGRST116") {
    throw error;
  }

  if (!data) return null;

  return {
    languageId: data.language_id,
    pace: data.pace,
    weeklyWords: data.weekly_words,
  };
}

/**
 * Speichert / aktualisiert die Einstellungen für ein Profil.
 * Wird z.B. aufgerufen, wenn du in der App Sprache & Tempo bestätigt hast.
 */
export async function saveProfileSettings(
  userId,
  profileId,
  { languageId, pace, weeklyWords }
) {
  if (!userId || !profileId) {
    throw new Error("saveProfileSettings: missing userId or profileId");
  }

  const { error } = await supabase
    .from("profile_settings")
    .upsert(
      {
        user_id: userId,
        profile_id: profileId,
        language_id: languageId,
        pace,
        weekly_words: weeklyWords,
      },
      { onConflict: "user_id,profile_id" }
    );

  if (error) {
    throw error;
  }
}

/**
 * Höchste freigeschaltete Woche eines Profils (Spalte `unlocked_week`).
 * Gilt nur für die gespeicherte Konfiguration (Sprache + Tempo) des Profils:
 * passt sie nicht zu `languageId`/`pace`, wird `null` zurückgegeben,
 * damit nach einem Sprach- oder Tempowechsel keine fremde Woche übernommen wird.
 * Gibt `null` zurück, wenn es noch keinen Datensatz gibt.
 * Wirft einen Fehler, falls die Spalte (noch) fehlt – der Aufrufer fällt dann auf localStorage zurück.
 */
export async function loadUnlockedWeekRemote(userId, profileId, languageId, pace) {
  if (!userId || !profileId) {
    throw new Error("loadUnlockedWeekRemote: missing userId or profileId");
  }

  const { data, error } = await supabase
    .from("profile_settings")
    .select("unlocked_week, language_id, pace")
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    throw error;
  }

  if (!data || data.language_id !== languageId || data.pace !== pace) {
    return null;
  }

  return data.unlocked_week ?? null;
}

/**
 * Speichert die höchste freigeschaltete Woche.
 * Nur Update (kein Upsert): der Datensatz entsteht beim Speichern der Lern-Einstellungen.
 * Greift nur, wenn Sprache + Tempo des Datensatzes zur aktuellen Konfiguration passen.
 */
export async function saveUnlockedWeekRemote(
  userId,
  profileId,
  languageId,
  pace,
  unlockedWeek
) {
  if (!userId || !profileId) {
    throw new Error("saveUnlockedWeekRemote: missing userId or profileId");
  }

  const { error } = await supabase
    .from("profile_settings")
    .update({ unlocked_week: unlockedWeek })
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .eq("language_id", languageId)
    .eq("pace", pace);

  if (error) {
    throw error;
  }
}

/**
 * Zeitpunkt, an dem die höchste Woche freigeschaltet wurde (`profile_settings.unlocked_at`).
 * Eigener Aufruf, damit die Freischaltung auch ohne diese Spalte weiter funktioniert.
 * @returns {Promise<{week: number, at: string} | null>}
 */
export async function loadUnlockedAtRemote(userId, profileId, languageId, pace) {
  if (!userId || !profileId) {
    throw new Error("loadUnlockedAtRemote: missing userId or profileId");
  }

  const { data, error } = await supabase
    .from("profile_settings")
    .select("unlocked_week, unlocked_at, language_id, pace")
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    throw error;
  }

  if (!data || data.language_id !== languageId || data.pace !== pace) {
    return null;
  }
  if (!data.unlocked_at) return null;

  return { week: data.unlocked_week ?? 1, at: data.unlocked_at };
}

export async function saveUnlockedAtRemote(userId, profileId, languageId, pace, at) {
  if (!userId || !profileId) {
    throw new Error("saveUnlockedAtRemote: missing userId or profileId");
  }

  const { error } = await supabase
    .from("profile_settings")
    .update({ unlocked_at: at })
    .eq("user_id", userId)
    .eq("profile_id", profileId)
    .eq("language_id", languageId)
    .eq("pace", pace);

  if (error) {
    throw error;
  }
}

/**
 * Setzt die freigeschaltete Woche zurück, z. B. nach einem Sprach- oder Tempowechsel.
 * Eigener Aufruf (nicht Teil von saveProfileSettings), damit das Speichern der
 * Einstellungen auch dann klappt, wenn die Spalte `unlocked_week` (noch) fehlt.
 */
export async function resetUnlockedWeekRemote(userId, profileId) {
  if (!userId || !profileId) {
    throw new Error("resetUnlockedWeekRemote: missing userId or profileId");
  }

  const { error } = await supabase
    .from("profile_settings")
    .update({ unlocked_week: null })
    .eq("user_id", userId)
    .eq("profile_id", profileId);

  if (error) {
    throw error;
  }

  // Zeitpunkt der letzten Freischaltung mit zurücksetzen (Montags-Regel), eigener Aufruf,
  // damit der Reset auch ohne die Spalte unlocked_at klappt
  const { error: atError } = await supabase
    .from("profile_settings")
    .update({ unlocked_at: null })
    .eq("user_id", userId)
    .eq("profile_id", profileId);
  if (atError) {
    console.warn("resetUnlockedWeekRemote: unlocked_at not reset:", atError.message || atError);
  }
}

/**
 * Persönliche Angaben (Vorname, Geschlecht, Geburtsdatum) laden – eine Zeile pro User
 * in `user_details`, unabhängig vom Lernprofil.
 * @param {string} userId
 * @returns {Promise<{firstName: string, gender: string, birthDate: string} | null>}
 *   null = noch keine Angaben gespeichert
 */
export async function loadUserDetails(userId) {
  if (!userId) throw new Error("loadUserDetails: missing userId");

  const { data, error } = await supabase
    .from("user_details")
    .select("first_name,gender,birth_date")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return {
    firstName: data.first_name,
    gender: data.gender,
    birthDate: data.birth_date, // "YYYY-MM-DD"
  };
}

/**
 * Persönliche Angaben speichern (Upsert, PK: user_id).
 * @param {string} userId
 * @param {{firstName: string, gender: string, birthDate: string, email?: string}} details
 */
export async function saveUserDetails(userId, details) {
  if (!userId) throw new Error("saveUserDetails: missing userId");

  const row = {
    user_id: userId,
    first_name: details.firstName,
    gender: details.gender,
    birth_date: details.birthDate,
    email: details.email ?? null,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from("user_details")
    .upsert(row, { onConflict: "user_id" });
  if (error) throw error;
}
